import type { UiDensity } from "../constants/density";

export interface User {
  _id: string;
  username?: string;
  name: string;
  role?: UserRole;
  timezone?: string;
  timezoneManual?: boolean;
  uiDensityDesktop?: UiDensity;
  uiDensityMobile?: UiDensity;
  profilePictureUrl?: string | null;
  isTempPassword: boolean;
  companyId: string;
  deactivated: boolean;
  initiallyCreatedFrom?: "MANUAL" | "LOGISLY";
  initialLogislyRef?: {
    entityType?: string;
    externalId?: string;
    label?: string;
  };
}

export enum UserRole {
  ADMIN = "ADMIN",
  SUPERUSER = "SU",
  USER = "USER",
  DRIVER = "DRIVER",
  STAFF = "STAFF",
  LIMITED = "LIMITED",
  STAFF_OPERATIONS = "STAFF_OPERATIONS",
  STAFF_FINANCE_VERIFIER = "STAFF_FINANCE_VERIFIER",
  STAFF_FINANCE_APPROVER = "STAFF_FINANCE_APPROVER",
  STAFF_FINANCE_INVOICING = "STAFF_FINANCE_INVOICING",
}

export const CREATE_USER_ROLES = [
  UserRole.ADMIN,
  UserRole.STAFF,
  UserRole.STAFF_OPERATIONS,
  UserRole.STAFF_FINANCE_VERIFIER,
  UserRole.STAFF_FINANCE_APPROVER,
  UserRole.STAFF_FINANCE_INVOICING,
  UserRole.LIMITED,
  UserRole.DRIVER,
] as const;

export const getClientTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Mapper functions
export const mapUserResponse = (response: any): User => {
  return {
    _id: response._id,
    username: response.username,
    name: response.name,
    role: response.role,
    timezone: response.timezone,
    timezoneManual: Boolean(response.timezoneManual),
    uiDensityDesktop: response.uiDensityDesktop,
    uiDensityMobile: response.uiDensityMobile,
    profilePictureUrl: response.profilePictureUrl ?? null,
    isTempPassword: response.isTempPassword,
    companyId: response.companyId,
    deactivated: response.deactivated || false,
    initiallyCreatedFrom: response.initiallyCreatedFrom || "MANUAL",
    initialLogislyRef: response.initialLogislyRef,
  };
};

export const mapUserLoginResponse = (response: any): { token: string; user: User } => {
  return {
    token: response.token,
    user: mapUserResponse(response.user),
  };
};

/**
 * Checks if a user has admin privileges (ADMIN or SUPERUSER role)
 * @param user - The user object to check
 * @returns true if the user's role is ADMIN or SUPERUSER, false otherwise
 */
export const isUserAdmin = (user: User | null | undefined): boolean => {
  if (!user?.role) return false;
  return user.role === UserRole.ADMIN || user.role === UserRole.SUPERUSER;
};

/**
 * Checks if a user is a SUPERUSER
 * @param user - The user object to check
 * @returns true if the user's role is SUPERUSER, false otherwise
 */
export const isUserSuperUser = (user: User | null | undefined): boolean => {
  if (!user?.role) return false;
  return user.role === UserRole.SUPERUSER;
};

export const isStaffOperations = (user: User | null | undefined): boolean => {
  return user?.role === UserRole.STAFF_OPERATIONS;
};

export const isFinanceVerifier = (user: User | null | undefined): boolean => {
  return user?.role === UserRole.STAFF_FINANCE_VERIFIER;
};

export const isFinanceApprover = (user: User | null | undefined): boolean => {
  return user?.role === UserRole.STAFF_FINANCE_APPROVER;
};

export const isFinance = (user: User | null | undefined): boolean => {
  return isFinanceVerifier(user) || isFinanceApprover(user);
};

export const isFinanceInvoicing = (user: User | null | undefined): boolean => {
  return user?.role === UserRole.STAFF_FINANCE_INVOICING;
};

export const canUseUangJalanRevamp = (user: User | null | undefined): boolean => {
  return (
    isUserAdmin(user) || isStaffOperations(user) || isFinance(user) || isFinanceInvoicing(user)
  );
};

export const canMutateOrders = (user: User | null | undefined): boolean => {
  return isUserAdmin(user) || isStaffOperations(user);
};

export const canMutateCustomers = (user: User | null | undefined): boolean => {
  return !!user?.role && user.role !== UserRole.DRIVER;
};

export const getDefaultLandingPath = (user: User | null | undefined): string => {
  if (isUserAdmin(user)) {
    return "/dashboard";
  }
  if (isFinanceInvoicing(user)) {
    return "/surat-jalan/action-required";
  }
  if (isFinance(user)) {
    return "/uang-jalan-reports/action-required";
  }
  return "/orders/action-required";
};

export const isAdminOrStaffUser = (user: User | null | undefined): boolean => {
  if (!user?.role) return false;
  return (
    isUserAdmin(user) || user.role === UserRole.STAFF || user.role === UserRole.STAFF_OPERATIONS
  );
};

export const canViewProfile = (
  actor: User | null | undefined,
  target: User | null | undefined,
): boolean => {
  if (!actor || !target) return false;
  if (actor.companyId !== target.companyId) return false;
  if (actor._id === target._id) return true;
  if (isUserSuperUser(actor)) return true;
  if (actor.role === UserRole.ADMIN) return true;
  if (isStaffOperations(actor) && target.role === UserRole.DRIVER) return true;
  return false;
};

export const canEditProfile = (
  actor: User | null | undefined,
  target: User | null | undefined,
): boolean => {
  if (!actor || !target) return false;
  if (actor.companyId !== target.companyId) return false;
  if (actor._id === target._id) return true;
  if (isUserSuperUser(actor)) return true;
  if (actor.role === UserRole.ADMIN && target.role !== UserRole.SUPERUSER) return true;
  if (isStaffOperations(actor) && target.role === UserRole.DRIVER) return true;
  return false;
};
